<template>
  <div class="container-fluid">
    <!-- Breadcrumb -->
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <!-- Header -->
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Internal User Role Management</h2>
          </div>

          <!-- Body -->
          <div class="body pt-0">
            <!-- Search and Filters -->
            <div
              class="chart-top-action d-lg-flex justify-content-between py-3"
            >
              <!-- Create User Button -->
              <template v-if="$store.getters['authpermission/hasAnyPermission'](['roles.create'])">
                <div class="ml-auto">
                  <b-button variant="primary" @click="showCreateRoleModal">
                    <i class="fa fa-plus"></i>
                    Create Role
                  </b-button>
                </div>
              </template>
            </div>

            <!-- Users Table -->
            <b-table
              responsive
              table-class="mb-0 table-custom border"
              head-variant="light"
              :items="items"
              :fields="fields"
              :per-page="perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @sort-changed="onSortChanged"
              striped
              show-empty
            >
              <!-- <template #head()="{ label, field: { key } }">
                {{ label }}
                <template>
                  <i v-if="sortBy !== key" class=""></i>
                  <i v-else-if="sortDesc" class="fa fa-long-arrow-down ml-1"></i>
                  <i v-else class="fa fa-long-arrow-up ml-1"></i>
                </template>
              </template> -->

              <template #cell(permission)="data">
                <button @click="viewPermission(data.item)" class="btn btn-primary">
                  <i class="fa fa-shield"></i> View 
                  <!-- ({{ data.item.totalpermission }}) -->
                </button>
              </template>
              <template #cell(action)="data">
                <b-dropdown
                  id="dropdown-action"
                  text="Actions"
                  right
                  variant="outline-primary"
                  size="sm"
                >
                  <template v-if="$store.getters['authpermission/hasAnyPermission'](['roles.edit'])">
                    <b-dropdown-item @click="editRole(data.item)"> Edit Role </b-dropdown-item>
                  </template>
                  <template v-if="$store.getters['authpermission/hasAnyPermission'](['roles.assign'])">
                    <b-dropdown-item @click="managePermission(data.item)"> Manage Permissions </b-dropdown-item>
                  </template>
                </b-dropdown>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create User Modal -->
    <create-user-modal
      ref="createRoleModal"
      :editMode="editMode"
      :roleToEdit="roleToEdit"
      @user-role-created="onRoleCreated"
    ></create-user-modal>

    <!-- View Menu Modal -->
    <!-- <view-menu-modal
      ref="viewMenuModal"
      :roleid="selectedRoleId"
      @view-menu="onViewMenu"
    ></view-menu-modal> -->

    <!-- View Permission Modal -->
    <view-permission-modal
      ref="viewPermissionModal"
      :roleid="selectedRoleId"
      @view-permission="onViewPermission"
    ></view-permission-modal>

    <!-- Manage Permission Modal -->
    <manage-permission-modal
      ref="managePermissionModal"
      :roleid="selectedRoleId"
      @manage-permission="onManagePermission"
    ></manage-permission-modal>

    <!-- Loader -->
    <main-loader :loading="isLoading"></main-loader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { GET_ROLE_LIST } from "../../../store/actions.type.js";
import CreateRoleModal from "./CreateRoleModal.vue";
// import ViewMenuModal from "./ViewMenuModal.vue";
import ViewPerissionModal from "./ViewPermissionModal.vue";
import ManagePermissionModal from "./ManagePermissionModal.vue";

const LoaderComponent =
  require("@/components/admin/loader/LoaderComponent.vue").default;
const BreadCrumb = require("@/components/BreadCrumb.vue").default;

export default {
  name: "UserRoleListComponent",
  components: {
    "main-loader": LoaderComponent,
    "bread-crumb": BreadCrumb,
    "create-user-modal": CreateRoleModal,
    // "view-menu-modal": ViewMenuModal,
    "view-permission-modal": ViewPerissionModal,
    "manage-permission-modal": ManagePermissionModal,
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Home", links: "/home" },
        { title: "User Role Management", isbold: true },
      ],
      fields: [
        { key: "role", label: "Role" },
        { key: "permission", label: "Permissions" },
        { key: "action", label: "Action" },
      ],
      items: [],
      currentPage: 1,
      perPage: 25,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      sortBy: "role",
      sortDesc: false,
      searchKeywords: "",
      currentPageJump: "",
      isLoading: false,
      selectedRoleId: null,
      editMode: false,
      roleToEdit: null,
    };
  },
  computed: {
    ...mapGetters("internalUser", ["users", "totalUsers", "loading"]),
  },
  watch: {
    currentPage() {
      this.fetchRoles();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchRoles();
    },
  },
  mounted() {
    this.fetchRoles();
  },
  methods: {
    ...mapActions("internalUser", [
      "setRoles",
      "setLoading",
      "setCurrentPage",
      "setPerPage",
      "setSort",
      "setSortBy",
      "setSearchKeyword",
    ]),

    async fetchRoles() {
      this.isLoading = true;
      this.setLoading(true);

      try {
        this.items = [];
        const roleListResponse = await this.$store.dispatch(GET_ROLE_LIST);
        const roles = roleListResponse.data.details.map((item) => ({
          roleid: item.id,
          role: item.name,
          totalpermission: null, // Will be filled after fetching
        }));

        // Fetch permission count for each role in parallel
        //await Promise.all(
        //   roles.map(async (role) => {
        //     try {
        //       const permission = await this.$store.dispatch(
        //         GET_ROLE_WISE_PERMISSION_LIST,
        //         role.roleid
        //       );
        //       // Defensive: check structure
        //       if (
        //         permission &&
        //         permission.data &&
        //         permission.data.details &&
        //         permission.data.details.length > 0
        //       ) {
        //         role.totalpermission = permission.data.details[0].totalpermission;
        //       } else {
        //         role.totalpermission = 0;
        //       }
        //     } catch (e) {
        //       role.totalpermission = 0;
        //     }
        //   })
        // );

        this.items = roles;
        this.setRoles(this.items);
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast.error(
          error.response?.data?.message ||
            "Failed to fetch users. Please try again.",
          { position: "top-right" },
        );
        this.items = [];
      } finally {
        this.isLoading = false;
        this.setLoading(false);
      }
    },

    searchRoles() {
      this.currentPage = 1;
      this.fetchRoles();
    },

    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.fetchRoles();
    },

    onEnter() {
      const pageNumber = parseInt(this.currentPageJump);
      const maxPages = Math.ceil(this.totalRows / this.perPage);

      if (pageNumber && pageNumber > 0 && pageNumber <= maxPages) {
        this.currentPage = pageNumber;
        this.currentPageJump = "";
      } else {
        this.$toast.warning(
          `Please enter a valid page number between 1 and ${maxPages}`,
          { position: "top-right" },
        );
      }
    },

    showCreateRoleModal() {
      this.editMode = false;
      this.roleToEdit = null;
      this.$refs.createRoleModal.show();
    },

    onRoleCreated() {
      this.editMode = false;
      this.roleToEdit = null;
      this.fetchRoles();
    },

    // viewMenu(item) {
    //   this.selectedRoleId = item.roleid; // Set selected roleid
    //   this.$refs.viewMenuModal.show();
    // },

    // onViewMenu() {
    //   this.fetchRoles();
    // },

    viewPermission(item) {
      this.selectedRoleId = item.roleid; // Set selected roleid
      this.$refs.viewPermissionModal.show();
    },
    onViewPermission(){
      this.fetchRoles();
    },

    managePermission(item) {
      this.selectedRoleId = item.roleid; // Set selected roleid
      this.$refs.managePermissionModal.show();
    },
    onManagePermission(){
      this.fetchRoles();
    },
    editRole(item) {
      this.editMode = true;
      this.roleToEdit = item;
      this.$refs.createRoleModal.show();
    },
  },
};
</script>


